/**
 * Socket.io temps réel pour /boutique/[slug] (calqué sur useEventTicketSocket
 * de /b/[slug]) :
 *  - room publique `shop:{shopId}` (join:shop / leave:shop)
 *  - event `shop:stock_update` → patch EN PLACE de `remaining` / `sold_out`
 *    sur les variantes de la boutique (source serveur, même définition que
 *    GET /api/public/shops/:slug)
 *
 * Le clamp du panier n'est PAS refait ici : useShopCart watch déjà les
 * `remaining` des variantes (deep) et ramène les quantités au plafond — on ne
 *  fait QUE le feedback (toast + retour au panier si la sélection a été
 * réduite). Une seule autorité de mutation, pattern /b. Chantier boutique
 * 2026-08-05.
 */
import { quantiteEnDirect, useToastEnDirect } from "~/composables/useToastEnDirect";
import { io, type Socket } from "socket.io-client";
import type { Shop } from "~/types/shop";

interface ShopStockUpdatePayload {
  shopId: string;
  products: Array<{
    id: string;
    variants: Array<{
      id: string;
      remaining: number | null;
      sold_out: boolean;
    }>;
  }>;
  /** Présent quand une commande vient d'être PAYÉE (cf. API emitShopStockUpdate). */
  achat?: { articles: number };
}

export function useShopSocket(opts: {
  shop: Ref<Shop | null>;
  /** Total d'articles au panier (useShopCart) — détection de réduction. */
  totalItems: Ref<number>;
  checkoutStep: Ref<number>;
}) {
  const { t } = useI18n();
  const config = useRuntimeConfig();
  const toast = useToast();
  const { annoncerEnDirect } = useToastEnDirect();
  let shopSocket: Socket | null = null;

  onMounted(() => {
    // Connexion dès que la boutique est connue — y compris si elle arrive
    // APRÈS le mount (retry client suite à une erreur SSR transitoire).
    // Init unique via flag (pattern useEventTicketSocket).
    let initialized = false;
    watch(
      () => opts.shop.value?.id,
      (shopId) => {
        if (!shopId || initialized) return;
        initialized = true;
        initSocket(String(shopId));
      },
      { immediate: true },
    );
  });

  function initSocket(shopId: string) {
    const socketUrl = config.public.apiBaseUrl || "http://localhost:4000";
    shopSocket = io(socketUrl, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 10,
    });

    shopSocket.on("connect", () => {
      shopSocket!.emit("join:shop", shopId);
    });

    shopSocket.on("shop:stock_update", (data: ShopStockUpdatePayload) => {
      const shop = opts.shop.value;
      if (!shop || data.shopId !== String(shop.id)) return;

      if (data.achat && data.achat.articles > 0) {
        annoncerEnDirect(
          t("commun.enDirect.vientDAcheterQuantite", {
            quantite: quantiteEnDirect(data.achat.articles, t("commun.enDirect.articleSingulier"), t("commun.enDirect.articlePluriel")),
          }),
          "i-lucide-shopping-bag",
        );
      }

      // Snapshot AVANT le patch : le clamp réactif de useShopCart va réduire
      // les quantités si une variante sélectionnée vient de s'épuiser.
      const itemsBefore = opts.totalItems.value;

      // Patch EN PLACE (réactif) des compteurs serveur sur les variantes.
      const byVariant = new Map<
        string,
        { remaining: number | null; sold_out: boolean }
      >();
      for (const product of data.products) {
        for (const v of product.variants) {
          byVariant.set(v.id, { remaining: v.remaining, sold_out: v.sold_out });
        }
      }
      for (const product of shop.products || []) {
        for (const variant of product.variants || []) {
          const update = byVariant.get(String(variant.id));
          if (!update) continue;
          variant.remaining = update.remaining;
          variant.sold_out = update.sold_out;
        }
      }

      // Feedback UNIQUEMENT (le clamp est la seule autorité de mutation) :
      // si la sélection a été réduite, prévenir l'acheteur et le ramener au
      // panier s'il était plus loin dans le tunnel (pattern /b).
      void nextTick(() => {
        if (opts.totalItems.value < itemsBefore) {
          toast.add({
            title: t("boutique.socket.toastVenteTitre"),
            description:
              t("boutique.socket.toastVenteDescription"),
            color: "warning",
            duration: 6000,
          });
          if (opts.checkoutStep.value > 1) opts.checkoutStep.value = 1;
        }
      });
    });
  }

  onUnmounted(() => {
    if (shopSocket) {
      if (opts.shop.value?.id) {
        shopSocket.emit("leave:shop", String(opts.shop.value.id));
      }
      shopSocket.disconnect();
      shopSocket = null;
    }
  });
}
